import { AppModule } from '@thoughtspot/blink-context';
import { AppLogoType } from '@thoughtspot/custom-style-service';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { getOnEnter } from '@thoughtspot/radiant-react/widgets/_utils/index';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Image } from '@thoughtspot/radiant-react/widgets/image';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { RoutedLink } from '@thoughtspot/radiant-react/widgets/link';
import cx from 'classnames';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useBlinkContextForGlobalNav, useValidateBlinkContextForGlobalNav, } from '../contexts/global-nav-blink-context';
import { HelpMenuContainer } from './help-menu/help-menu.container';
import { OrgMenuContainer } from './org-menu/org-menu.container';
import styles from './top-nav-v3.module.scss';
export var TopNavV3TestIDs;
(function (TopNavV3TestIDs) {
    TopNavV3TestIDs["HomeLink"] = "home-link";
})(TopNavV3TestIDs || (TopNavV3TestIDs = {}));
const AppLogoDefault = '/resources/img/logo/TS-logo-white-no-bg.svg';
export const TopNavV3 = ({ homeButtonConfig, searchBarV3, userProfile, shouldShowPendoHelp, isEmbedded = false, learningModule, isOverlayLeftPanel = false, inAppNotification, hamburgerIconHoverHandler = undefined, invalidateSessionInfoCallCache, currentAppModule, }) => {
    useValidateBlinkContextForGlobalNav();
    const { t } = useTranslation();
    const { sessionService, metricsService, customStylingService, navService, } = useBlinkContextForGlobalNav();
    const areProfileAndHelpHidden = sessionService.areProfileAndHelpHidden();
    const isOrgEnabled = sessionService.isOrgUiEnabled() && !sessionService.hideOrgSwitcherUi();
    const getCurrentPathByLocationHash = (includeQueryParams) => {
        return navService.getCurrentPathByLocationHash(includeQueryParams);
    };
    const hasCustomLogo = customStylingService
        .getConfig()
        ?.hasCustomImageLogo(AppLogoType.DEFAULT);
    const logo = customStylingService
        .getConfig()
        ?.getAppLogoUrl(AppLogoType.DEFAULT, AppLogoDefault);
    const navBackground = customStylingService
        .getConfig()
        ?.getAppPanelColor(true).color;
    const navBackgroundRef = useRef(null);
    useLayoutEffect(() => {
        if (navBackgroundRef.current) {
            navBackgroundRef.current.style.setProperty('background-color', `var(--ts-var-nav-background, ${navBackground})`, 'important');
        }
    }, [navBackgroundRef.current]);
    const showTopNavV3ContainerOnMouseLeave = () => {
        hamburgerIconHoverHandler(true);
    };
    const hideTopNavV3ContainerOnMouseLeave = () => {
        hamburgerIconHoverHandler(false);
    };
    const stopTsLogoRedirect = () => {
        if (sessionService.isApplicationSwitcherHidden() &&
            currentAppModule !== AppModule.Insights) {
            return true;
        }
        return false;
    };
    useEffect(() => {
        const el = navBackgroundRef.current;
        if (el) {
            el.addEventListener('mouseenter', showTopNavV3ContainerOnMouseLeave);
            el.addEventListener('mouseleave', hideTopNavV3ContainerOnMouseLeave);
        }
        return () => {
            if (el) {
                el.removeEventListener('mouseenter', showTopNavV3ContainerOnMouseLeave);
                el.removeEventListener('mouseleave', hideTopNavV3ContainerOnMouseLeave);
            }
        };
    }, []);
    const HomeButtonContent = React.useMemo(() => {
        const clickHandler = () => {
            if (stopTsLogoRedirect()) {
                return;
            }
            navService.goToHome();
            metricsService.collectEvent(new UserAction(userActionTypeEnum.TOP_NAV_LOGO_CLICK), {
                isDefaultLogo: true,
            });
        };
        const homeHref = `#${homeButtonConfig.target.routerURI ??
            homeButtonConfig.target.uri}` || '/';
        return (React.createElement(Horizontal, { className: styles.homeLink, vAlignContent: "center", basis: "224px" },
            React.createElement(RoutedLink, { href: homeHref, onClick: clickHandler, noUderline: true, tabIndex: 0, onKeyDown: getOnEnter(clickHandler), "aria-label": t('Thoughtspot.home'), "data-testid": TopNavV3TestIDs.HomeLink, color: "black" },
                React.createElement(Image, { className: cx(styles.tsLogo, {
                        [styles.tsLogoV3]: !isEmbedded && AppLogoDefault === logo,
                        [styles.noTsLogoV3Redirect]: stopTsLogoRedirect(),
                        [styles.hasCustomLogo]: hasCustomLogo,
                    }), src: logo, fallbackSrc: AppLogoDefault }))));
    }, [homeButtonConfig]);
    const HamburgerIcon = React.useMemo(() => {
        return (React.createElement(Vertical, { onMouseOver: () => {
                if (!sessionService.isHambugerHidden())
                    hamburgerIconHoverHandler?.(true, true);
            }, "data-testid": "hamburger-icon", className: cx(styles.hamburgerIconContainer, {
                [styles.hamburgerIconContainerOverlay]: isOverlayLeftPanel &&
                    !sessionService.isHambugerHidden(),
            }), onClick: () => {
                metricsService.collectEvent(new UserAction(userActionTypeEnum.HAMBURGER_ICON_CLICK), {});
            } },
            React.createElement(Icon, { className: styles.hamburgerIcon, id: IconID.HAMBURGER, color: IconColor.WHITE, size: IconSize.LARGE })));
    }, [isOverlayLeftPanel]);
    return (React.createElement("div", { className: styles.topNavV3, ref: navBackgroundRef },
        React.createElement(Horizontal, { vAlignContent: "center", className: cx(styles.humburgerPlusLogo, {
                [styles.humburgerShow]: isOverlayLeftPanel &&
                    !sessionService.isHambugerHidden(),
            }) },
            HamburgerIcon,
            HomeButtonContent),
        React.createElement(Horizontal, { grow: true, hAlignContent: "right", className: styles.searchBarWrapper }, !sessionService.isObjectSearchHidden() &&
            searchBarV3 &&
            searchBarV3),
        !sessionService.isNotificationHidden() && inAppNotification,
        !areProfileAndHelpHidden && (React.createElement(Vertical, { className: styles.helpMenuContainer },
            React.createElement(HelpMenuContainer, { isPendoHelpEnabled: shouldShowPendoHelp, learningModule: learningModule }))),
        !isOrgEnabled ? (userProfile && !areProfileAndHelpHidden && userProfile) : (React.createElement(Horizontal, { spacing: "a", grow: 0, shrink: 0, hAlignContent: "right" },
            React.createElement(OrgMenuContainer, { isEmbedded: isEmbedded, invalidateSessionInfoCallCache: invalidateSessionInfoCallCache }),
            userProfile && !areProfileAndHelpHidden && userProfile))));
};
//# sourceMappingURL=top-nav-v3.container.js.map